import { useState, useEffect } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { Table, Tag, Popconfirm } from "antd";
import { Inbox, Trash2, RefreshCw, CheckCircle, Phone, Clock, AlertTriangle, XCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import {
    AI_CALLER_BUCKET_URL,
    AI_CALLER_BUCKET_ITEM_URL,
    AI_CALLER_BUCKET_CLEAR_URL,
} from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const STATUS_CONFIG = {
    pending:   { color: "blue",    icon: Clock,          label: "Pending" },
    calling:   { color: "green",   icon: Phone,          label: "Calling" },
    completed: { color: "default", icon: CheckCircle,    label: "Completed" },
    exhausted: { color: "orange",  icon: AlertTriangle,  label: "Exhausted" },
};

const PRIORITY_COLORS = {
    high:   "red",
    medium: "blue",
    low:    "default",
};

const OUTCOME_COLORS = {
    demo_booked:        "green",
    qualified:          "cyan",
    callback_requested: "blue",
    not_interested:     "default",
    disqualified:       "default",
    no_answer:          "orange",
    voicemail:          "orange",
    error:              "red",
    no_phone:           "red",
};

export default function AICallerBucket() {
    const { fetch, create, remove } = useApiMethods();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [summary, setSummary] = useState({});
    const [statusFilter, setStatusFilter] = useState(null);
    const [page, setPage] = useState(1);

    const loadBucket = async (pg = page, sf = statusFilter) => {
        setLoading(true);
        const params = { page: pg, limit: 50 };
        if (sf) params.status = sf;
        const res = await fetch(AI_CALLER_BUCKET_URL, params);
        if (res?.status === "success") {
            setItems(res.data || []);
            setTotalCount(res.total_count || 0);
            setSummary(res.summary || {});
        }
        setLoading(false);
    };

    useEffect(() => { loadBucket(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRemove = async (id) => {
        const res = await remove(AI_CALLER_BUCKET_ITEM_URL(id));
        if (res?.status === "success") {
            ToastMsg("success", "Removed from bucket");
            loadBucket();
        } else {
            ToastMsg("error", res?.message || "Failed to remove");
        }
    };

    const handleClear = async () => {
        const res = await create(AI_CALLER_BUCKET_CLEAR_URL, {});
        if (res?.status === "success") {
            ToastMsg("success", res.message);
            loadBucket();
        } else {
            ToastMsg("error", res?.message || "Failed to clear");
        }
    };

    const handleStatusFilter = (val) => {
        setStatusFilter(val || null);
        setPage(1);
        loadBucket(1, val || null);
    };

    const columns = [
        {
            title: "Customer",
            key: "customer",
            render: (_, r) => (
                <div>
                    <div className="font-medium text-gray-800 dark:text-gray-100">{r.customer_name || "—"}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{r.customer_phone || "—"}</div>
                </div>
            ),
        },
        {
            title: "Lead Stage",
            dataIndex: "customer_lead_stage",
            key: "lead_stage",
            render: (val) => val ? <Tag>{val}</Tag> : "—",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (val) => {
                const cfg = STATUS_CONFIG[val] || {};
                return (
                    <Tag color={cfg.color || "default"}>
                        {val === "calling" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse mr-1" />}
                        {(cfg.label || val || "").toUpperCase()}
                    </Tag>
                );
            },
        },
        {
            title: "Priority",
            dataIndex: "priority",
            key: "priority",
            render: (val) => <Tag color={PRIORITY_COLORS[val] || "default"}>{(val || "").toUpperCase()}</Tag>,
        },
        {
            title: "Attempts",
            key: "attempts",
            render: (_, r) => (
                <span className="text-sm text-gray-600 dark:text-gray-300">
                    {r.attempt_count}/{r.max_attempts}
                </span>
            ),
        },
        {
            title: "Last Outcome",
            dataIndex: "last_outcome",
            key: "last_outcome",
            render: (val) => val
                ? <Tag color={OUTCOME_COLORS[val] || "default"}>{(val || "").replace("_", " ").toUpperCase()}</Tag>
                : <span className="text-gray-400">—</span>,
        },
        {
            title: "Last Attempt",
            dataIndex: "last_attempt_at",
            key: "last_attempt_at",
            render: (val) => val ? dayjs(val).fromNow() : "—",
        },
        {
            title: "Added",
            dataIndex: "created_date",
            key: "created_date",
            render: (val) => val ? dayjs(val).format("DD MMM, h:mm A") : "—",
        },
        {
            title: "",
            key: "actions",
            width: 50,
            render: (_, r) => (
                <Popconfirm title="Remove from bucket?" onConfirm={() => handleRemove(r.id)} okText="Yes" cancelText="No">
                    <button className="text-gray-400 hover:text-red-500" onClick={(e) => e.stopPropagation()}>
                        <Trash2 className="h-4 w-4" />
                    </button>
                </Popconfirm>
            ),
        },
    ];

    const completedCount = (summary.completed || 0) + (summary.exhausted || 0);

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center justify-between mb-4 p-2">
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                        <Inbox className="h-5 w-5 text-teal-600" /> Call Bucket
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        Leads pushed for AI calling — auto-called until a result
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {completedCount > 0 && (
                        <Popconfirm title={`Clear ${completedCount} completed/exhausted items?`} onConfirm={handleClear} okText="Yes" cancelText="No">
                            <Button size="sm" variant="outline" className="text-xs flex items-center gap-1">
                                <XCircle className="h-3 w-3" /> Clear Done ({completedCount})
                            </Button>
                        </Popconfirm>
                    )}
                    <Button onClick={() => loadBucket()} size="sm" variant="outline" className="text-xs flex items-center gap-1" disabled={loading}>
                        <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3 px-2 mb-4">
                {[
                    { key: "pending",   label: "Pending",   icon: Clock,         color: "text-blue-600",   bg: "bg-blue-50 dark:bg-blue-900/20" },
                    { key: "calling",   label: "Calling",   icon: Phone,         color: "text-green-600",  bg: "bg-green-50 dark:bg-green-900/20" },
                    { key: "completed", label: "Completed", icon: CheckCircle,   color: "text-gray-600",   bg: "bg-gray-50 dark:bg-gray-800" },
                    { key: "exhausted", label: "Exhausted", icon: AlertTriangle, color: "text-orange-600", bg: "bg-orange-50 dark:bg-orange-900/20" },
                ].map(({ key, label, icon: Icon, color, bg }) => (
                    <button
                        key={key}
                        onClick={() => handleStatusFilter(statusFilter === key ? null : key)}
                        className={`${bg} rounded-lg p-3 text-left transition-all ${statusFilter === key ? "ring-2 ring-teal-500" : ""}`}
                    >
                        <div className="flex items-center justify-between">
                            <Icon className={`h-4 w-4 ${color}`} />
                            <span className={`text-xl font-bold ${color}`}>{summary[key] || 0}</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</p>
                    </button>
                ))}
            </div>

            {/* Table */}
            <div className="px-2 mb-2">
                <Table
                    columns={columns}
                    dataSource={items}
                    rowKey="id"
                    loading={loading}
                    size="small"
                    locale={{ emptyText: "No leads in bucket. Select leads from Customer Directory and click 'AI Caller' to push them here." }}
                    pagination={{
                        current: page,
                        pageSize: 50,
                        total: totalCount,
                        showSizeChanger: false,
                        onChange: (pg) => { setPage(pg); loadBucket(pg); },
                    }}
                />
            </div>
        </ContainerCard>
    );
}
